import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { submitCheckIn, uploadProgressPhoto, getUserCheckIns, parsePhotoType } from '../services/checkInService';
import { toastError } from '../ui/toast';
import WeightRuler from './ui/WeightRuler';
import PhotoScan from './bodystats/PhotoScan';
import { Dialog } from './bodystats/parts';
import { latestScan, shrinkPhoto } from '../services/bodyScanService';
import { weighIns, firstWeighIn } from '../services/weightService';
import { MUSCLE_KINDS } from '../utils/bodyStats';
import { colors as theme, fonts } from '../theme';

const isNative = Capacitor.isNativePlatform();

// The monthly check-in in four short steps, in the style of the session screens (SessionMoments): weight
// and an optional body scan, photos, a message, then a "sent" screen. What is saved is unchanged.
// Design approved by the owner on 21 October 2026 (the "Monthly check-in, premium look" canvas).
const colors = {
  bg: theme.bg,
  surface1: theme.surface1,
  surface2: theme.surface2,
  surface3: theme.surface3,
  line: '#2A3566',
  empty: '#6B7290',
  text: theme.text,
  textMuted: theme.textMuted,
  gold: theme.gold,
  success: theme.success,
  error: theme.danger,
};

const STEPS = 4; // weight, photos, message, sent
const PHOTO_TYPES = [['front', 'Front'], ['side', 'Side'], ['back', 'Back']];

const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const shortDate = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const kgDiff = (a, b) => Math.round((a - b) * 10) / 10;

// A phone photo is made smaller on the phone (the body scan's own shrinking: longest side 1600 px, a
// JPEG under 2 MB) instead of being refused for being over 5 MB, as most phone photos are.
const asSmallJpeg = async (file, type) => {
  const { base64, preview } = await shrinkPhoto(file);
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return { file: new File([bytes], `${type}.jpg`, { type: 'image/jpeg' }), preview };
};

// The photos of one check-in by position, read from their stored names (…_front.jpg)
const byType = (checkIn) => {
  const map = {};
  (checkIn?.photo_urls || []).forEach((url) => {
    const type = parsePhotoType(url);
    if (type && !map[type]) map[type] = url;
  });
  return map;
};

const kicker = { fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.textMuted };
const heading = { margin: '8px 0 4px', fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, color: colors.text, outline: 'none' };
const lead = { margin: 0, fontSize: 15, lineHeight: 1.5, color: colors.textMuted };
const card = { padding: 16, borderRadius: 14, background: colors.surface1, display: 'flex', alignItems: 'center', gap: 14 };
const primaryBtn = {
  width: '100%', height: 56, borderRadius: 16, border: 'none', background: colors.gold, color: colors.bg,
  fontSize: 17, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
const quietBtn = {
  width: '100%', minHeight: 44, border: 'none', background: 'transparent', color: colors.textMuted,
  fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const smallBtn = {
  height: 44, padding: '0 16px', borderRadius: 12, border: `1px solid ${colors.line}`, background: 'transparent',
  color: colors.gold, fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
};
const iconBtn = {
  width: 44, height: 44, border: 'none', background: 'transparent', color: colors.textMuted, padding: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer',
};

const css = `
@keyframes ciRise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes ciDraw { from { stroke-dashoffset: 465; } to { stroke-dashoffset: 0; } }
@keyframes ciPop { from { opacity: 0; transform: scale(.6); } to { opacity: 1; transform: none; } }
.ci-rise { animation: ciRise .4s cubic-bezier(.2,.8,.2,1) both; }
.ci-draw { animation: ciDraw .9s cubic-bezier(.2,.8,.2,1) .1s both; }
.ci-pop { animation: ciPop .35s cubic-bezier(.2,1.4,.4,1) .7s both; }
.ci-bar { transition: background-color .3s; }
@media (prefers-reduced-motion: reduce) {
  .ci-rise, .ci-draw, .ci-pop { animation: none; }
  .ci-bar { transition: none; }
}
`;

const ScanIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M9 8h6M9 12h6M9 16h3" />
  </svg>
);
const CameraIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8a2 2 0 0 1 2-2h2l2-2h6l2 2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><circle cx="12" cy="13" r="3.5" />
  </svg>
);

const MonthlyCheckIn = ({ userId, weekNumber, onClose, onSuccess }) => {
  const [step, setStep] = useState(0);
  const [bodyweight, setBodyweight] = useState('');
  const [message, setMessage] = useState('');
  const [photos, setPhotos] = useState({ front: null, side: null, back: null });
  const [photoFiles, setPhotoFiles] = useState({ front: null, side: null, back: null });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const headingRef = useRef(null);

  // An optional body scan in the check-in (Body stats, plan 008). It is saved on its own when the person
  // taps Save in the scan steps, whatever happens to the check-in. A scan taken today with a weight fills
  // in the bodyweight, and then the check-in adds no second weigh-in for the same number.
  const [scanOpen, setScanOpen] = useState(false);
  const [scan, setScan] = useState(null);
  const [lastMachine, setLastMachine] = useState(null);
  // The weight starts at the last weigh-in, not at 70 kg (21 October 2026); the first weigh-in gives
  // "since you started" on the sent screen. Last month's photos guide the pose; the first ones are
  // shown beside today's when the check-in is sent.
  const [lastWeighIn, setLastWeighIn] = useState(null);
  const [startWeighIn, setStartWeighIn] = useState(null);
  const [lastPhotos, setLastPhotos] = useState({});
  const [firstPhotos, setFirstPhotos] = useState({});
  useEffect(() => {
    let gone = false;
    const quietly = (fn, use) => Promise.resolve().then(fn).then((v) => { if (!gone) use(v); }).catch(() => {});
    quietly(() => latestScan(userId), (s) => setLastMachine(s?.machine ?? null));
    quietly(() => weighIns(userId, 12), (rows) => {
      const last = rows[rows.length - 1];
      if (!last || !(last.weight > 0)) return;
      setLastWeighIn(last);
      setBodyweight((typed) => typed || String(last.weight));
    });
    quietly(() => firstWeighIn(userId), setStartWeighIn);
    quietly(() => getUserCheckIns(userId), (list) => {
      const withPhotos = (list || []).filter((c) => c.photo_urls?.length > 0);
      setFirstPhotos(byType(withPhotos[0]));
      setLastPhotos(byType(withPhotos[withPhotos.length - 1]));
    });
    return () => { gone = true; };
  }, [userId]);

  // Each step announces itself: focus moves to its heading
  useEffect(() => { headingRef.current?.focus(); }, [step]);

  const scanWeighsToday = scan?.weight_kg != null && scan.measured_on === todayIso();
  const scanSaved = (saved) => {
    setScanOpen(false);
    setScan(saved);
    if (saved?.weight_kg != null && saved.measured_on === todayIso()) setBodyweight(String(saved.weight_kg));
  };

  const takePhoto = async (type, picked) => {
    try {
      const { file, preview } = await asSmallJpeg(picked, type);
      setError(null);
      setPhotoFiles((prev) => ({ ...prev, [type]: file }));
      setPhotos((prev) => ({ ...prev, [type]: preview }));
    } catch (err) {
      setError(err.message || 'That photo could not be opened. Try another one.');
    }
  };

  const handlePhotoChange = (type, event) => {
    const picked = event.target.files[0];
    event.target.value = ''; // the same photo can be picked again after removing it
    if (picked) takePhoto(type, picked);
  };

  const handleRemovePhoto = (type) => {
    setPhotos((prev) => ({ ...prev, [type]: null }));
    setPhotoFiles((prev) => ({ ...prev, [type]: null }));
  };

  // Native camera/gallery picker (Capacitor)
  const handleNativePhotoSelect = async (type) => {
    try {
      const image = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt, // lets user choose camera or gallery
      });
      const mimeType = `image/${image.format}`;
      const bytes = Uint8Array.from(atob(image.base64String), (c) => c.charCodeAt(0));
      await takePhoto(type, new File([bytes], `${type}.${image.format}`, { type: mimeType }));
    } catch (err) {
      // User cancelled — not an error
      if (err?.message !== 'User cancelled photos app') {
        console.error('[Camera]', err);
      }
    }
  };

  const handleSubmit = async () => {
    if (!bodyweight || parseFloat(bodyweight) <= 0) {
      setError('Please enter a valid bodyweight');
      setStep(0);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Upload photos — any failure blocks the submit
      const photoUrls = [];
      for (const [type, file] of Object.entries(photoFiles)) {
        if (file) {
          const url = await uploadProgressPhoto(file, userId, type);
          if (!url) throw new Error('storage: photo upload returned no URL');
          photoUrls.push(url);
        }
      }

      // Submit check-in
      await submitCheckIn(userId, {
        bodyweightKg: parseFloat(bodyweight),
        weighedByScan: scanWeighsToday && parseFloat(bodyweight) === Number(scan.weight_kg),
        photoUrls,
        messageToCoach: message || null,
        weekNumber,
      });

      onSuccess?.();
      setStep(3);
    } catch (err) {
      console.error('Error submitting check-in:', err);

      // Provide more specific error messages
      let errorMsg;
      if (err.message?.includes('exceeded the maximum allowed size')) {
        errorMsg = 'One or more photos are too large. Please use smaller images.';
      } else if (err.message?.includes('storage')) {
        errorMsg = 'Photo upload failed. Check your connection and try again.';
      } else {
        errorMsg = 'Failed to submit check-in. Please try again.';
      }
      setError(errorMsg);
      toastError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const weight = parseFloat(bodyweight);
  const photoCount = PHOTO_TYPES.filter(([type]) => photos[type]).length;
  const month = Math.max(1, Math.ceil(weekNumber / 4));
  const sinceLast = lastWeighIn && weight > 0 ? kgDiff(weight, lastWeighIn.weight) : null;
  const sinceStart = startWeighIn && weight > 0 ? kgDiff(weight, startWeighIn.weight) : null;
  const sinceWords = (d) => (d === 0 ? 'the same' : `${Math.abs(d)} kg ${d < 0 ? 'less' : 'more'}`);

  const headingProps = { ref: headingRef, tabIndex: -1, style: heading, id: 'checkin-heading' };

  const progress = (
    <div role="img" aria-label={`Step ${Math.min(step + 1, 3)} of 3`} style={{ display: 'flex', gap: 6 }}>
      {[0, 1, 2].map((i) => (
        <span key={i} className="ci-bar" style={{ width: 28, height: 4, borderRadius: 2, background: i <= step ? colors.gold : colors.empty }} />
      ))}
    </div>
  );

  const topBar = step < 3 && (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 44 }}>
      {step === 0 ? (
        <button type="button" onClick={onClose} disabled={uploading} aria-label="Close check-in" style={iconBtn}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      ) : (
        <button type="button" onClick={() => setStep(step - 1)} disabled={uploading} aria-label="Back" style={iconBtn}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      )}
      {progress}
      <span style={{ width: 44 }} />
    </div>
  );

  const errorBanner = error && (
    <div role="alert" style={{ marginTop: 16, padding: '12px 14px', borderRadius: 12, background: 'rgba(255, 69, 58, 0.1)', border: '1px solid rgba(255, 69, 58, 0.3)', color: colors.error, fontSize: 13, lineHeight: 1.5 }}>
      {error}
    </div>
  );

  const weightStep = (
    <>
      <div className="ci-rise" style={{ marginTop: 28 }}>
        <div style={kicker}>Monthly check-in · Month {month}</div>
        <h2 {...headingProps}>Today's weight</h2>
        <p style={lead}>Weigh in the morning, before breakfast, for numbers you can compare.</p>
      </div>
      {errorBanner}
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12, padding: '20px 0' }}>
        <WeightRuler value={weight || 70} onChange={(v) => setBodyweight(String(v))} min={30} max={300} />
        {sinceLast != null && (
          <p style={{ margin: 0, textAlign: 'center', fontSize: 13, color: colors.textMuted }}>
            {sinceWords(sinceLast) === 'the same' ? 'The same as' : `${sinceWords(sinceLast)} than`} your last weigh-in ({lastWeighIn.weight} kg on {shortDate(lastWeighIn.date)}).
            {!scanWeighsToday && ' Slide to today\'s weight.'}
          </p>
        )}
      </div>
      <div style={{ ...card, marginBottom: 20 }}>
        <span style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(212, 175, 55, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ScanIcon /></span>
        {scan ? (
          <div role="status" style={{ flexGrow: 1, minWidth: 0, fontSize: 13, lineHeight: 1.5, color: colors.text }}>
            Scan added: {[
              scan.machine,
              scan.weight_kg != null && `${scan.weight_kg} kg`,
              scan.body_fat_pct != null && `${scan.body_fat_pct} % body fat`,
              scan.muscle_kg != null && `${scan.muscle_kg} kg ${(MUSCLE_KINDS[scan.muscle_kind] || 'muscle').toLowerCase()}`,
            ].filter(Boolean).join(' · ')}
            {scanWeighsToday && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>Your bodyweight above is from this scan.</div>}
          </div>
        ) : (
          <>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>Body scan · optional</div>
              <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 2, lineHeight: 1.4 }}>Add the sheet or your scale's screenshot; today's scan fills in your weight.</div>
            </div>
            <button type="button" onClick={() => setScanOpen(true)} aria-label="Add body scan" style={smallBtn}>Add</button>
          </>
        )}
      </div>
      <button type="button" onClick={() => { setError(null); setStep(1); }} disabled={!(weight > 0)} style={{ ...primaryBtn, opacity: weight > 0 ? 1 : 0.5 }}>Next</button>
    </>
  );

  const photoSlot = ([type, label]) => {
    const guide = lastPhotos[type];
    return (
      <div key={type} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        {photos[type] ? (
          <div style={{ position: 'relative', height: 168, borderRadius: 14, overflow: 'hidden', background: colors.surface2 }}>
            <img src={photos[type]} alt={`${type} view`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <button type="button" onClick={() => handleRemovePhoto(type)} disabled={uploading} aria-label={`Remove ${type} photo`}
              style={{ position: 'absolute', top: 4, right: 4, width: 36, height: 36, borderRadius: 18, border: 'none', background: 'rgba(10, 14, 39, 0.75)', color: colors.text, fontSize: 18, cursor: 'pointer' }}>×</button>
          </div>
        ) : (() => {
          const face = (
            <span style={{ position: 'relative', height: 168, borderRadius: 14, border: `1.5px dashed ${colors.empty}`, background: colors.surface1, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: colors.gold, cursor: 'pointer' }}>
              {guide && <img src={guide} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.28 }} />}
              <span style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <CameraIcon />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Add</span>
                {guide && <span style={{ fontSize: 11, color: colors.text, textAlign: 'center', padding: '0 6px' }}>Match this pose</span>}
              </span>
            </span>
          );
          return isNative ? (
            <button type="button" onClick={() => handleNativePhotoSelect(type)} disabled={uploading} aria-label={`Add ${type} photo`}
              style={{ padding: 0, border: 'none', background: 'none', display: 'flex', flexDirection: 'column' }}>{face}</button>
          ) : (
            <label aria-label={`Add ${type} photo`} style={{ display: 'flex', flexDirection: 'column' }}>
              <input type="file" accept="image/*" onChange={(e) => handlePhotoChange(type, e)} style={{ display: 'none' }} disabled={uploading} />
              {face}
            </label>
          );
        })()}
        <span style={{ fontSize: 12, fontWeight: 600, textAlign: 'center', color: photos[type] ? colors.text : colors.textMuted }}>{label}</span>
      </div>
    );
  };

  const photosStep = (
    <>
      <div className="ci-rise" style={{ marginTop: 28 }}>
        <div style={kicker}>Progress photos · optional</div>
        <h2 {...headingProps}>Three quick photos</h2>
        <p style={lead}>Same place, light and pose as last month, so the change is real.</p>
      </div>
      {errorBanner}
      <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
        {PHOTO_TYPES.map(photoSlot)}
      </div>
      <div style={{ ...card, marginTop: 20, alignItems: 'flex-start', padding: '14px 16px', gap: 12 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: colors.textMuted }}>Only you and your coach see your photos.</p>
      </div>
      <div style={{ flexGrow: 1 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
        <button type="button" onClick={() => { setError(null); setStep(2); }} style={primaryBtn}>Next</button>
        {photoCount === 0 && <button type="button" onClick={() => setStep(2)} style={quietBtn}>Skip photos this month</button>}
      </div>
    </>
  );

  const summaryCell = (value, label) => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: fonts.mono, fontSize: 18, fontWeight: 600, color: colors.text }}>{value}</div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );

  const messageStep = (
    <>
      <div className="ci-rise" style={{ marginTop: 28 }}>
        <div style={kicker}>Last step</div>
        <h2 {...headingProps}>Anything for your coach?</h2>
        <p style={lead}>Wins, struggles, sleep, a sore knee: whatever helps them plan your next month.</p>
      </div>
      {errorBanner}
      <label htmlFor="checkin-message" style={{ marginTop: 24, fontSize: 13, fontWeight: 600, color: colors.text }}>Message to your coach (optional)</label>
      <textarea
        id="checkin-message"
        placeholder="Any concerns, questions, or feedback?"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={uploading}
        rows={5}
        style={{ marginTop: 8, boxSizing: 'border-box', width: '100%', padding: '14px 16px', borderRadius: 14, border: `1px solid ${colors.line}`, background: colors.surface1, color: colors.text, fontFamily: 'inherit', fontSize: 15, lineHeight: 1.5, resize: 'none', outline: 'none' }}
      />
      <div style={{ flexGrow: 1, minHeight: 20 }} />
      <div style={{ padding: '14px 16px', borderRadius: 14, border: `1px solid ${colors.line}`, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginBottom: 16 }}>
        {summaryCell(weight > 0 ? weight : '—', 'kg')}
        {summaryCell(`${photoCount} of 3`, 'photos')}
        {summaryCell(scan ? '✓' : '—', 'scan')}
      </div>
      <button type="button" onClick={handleSubmit} disabled={uploading} style={{ ...primaryBtn, opacity: uploading ? 0.6 : 1 }}>
        {uploading ? 'Sending…' : 'Send check-in'}
      </button>
    </>
  );

  const comparison = PHOTO_TYPES.map(([type]) => type).find((type) => firstPhotos[type] && photos[type]);
  const sentStep = (
    <>
      <div style={{ height: 24 }} />
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ position: 'relative', width: 152, height: 152 }}>
          <svg width="152" height="152" viewBox="0 0 168 168" aria-hidden="true" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="84" cy="84" r="74" fill="none" stroke={colors.surface2} strokeWidth="12" />
            <circle className="ci-draw" cx="84" cy="84" r="74" fill="none" stroke={colors.gold} strokeWidth="12" strokeLinecap="round" strokeDasharray="465" strokeDashoffset="0" />
          </svg>
          <div className="ci-pop" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={colors.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
          </div>
        </div>
        <div style={{ ...kicker, marginTop: 24 }}>Month {month} done</div>
        <h2 {...headingProps}>Check-in sent</h2>
        <p style={{ ...lead, maxWidth: 300 }}>Your coach reviews it, and their feedback appears in Stats › Check-Ins.</p>

        {comparison && (
          <div className="ci-rise" style={{ marginTop: 24, width: '100%', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            {[[firstPhotos[comparison], 'Your first photo'], [photos[comparison], 'Today']].map(([src, label]) => (
              <figure key={label} style={{ margin: 0 }}>
                <img src={src} alt={`${label}, ${comparison} view`} style={{ width: '100%', height: 190, objectFit: 'cover', borderRadius: 14, display: 'block', background: colors.surface2 }} />
                <figcaption style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: colors.textMuted }}>{label}</figcaption>
              </figure>
            ))}
          </div>
        )}

        {sinceStart != null && startWeighIn && (
          <div style={{ marginTop: 16, width: '100%', padding: '14px 16px', boxSizing: 'border-box', borderRadius: 14, background: colors.surface1, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 13, color: colors.textMuted }}>Since you started ({shortDate(startWeighIn.date)})</span>
            <span style={{ fontFamily: fonts.mono, fontSize: 18, fontWeight: 600, color: colors.text }}>
              {sinceStart === 0 ? 'no change' : `${sinceStart > 0 ? '+' : '−'}${Math.abs(sinceStart)} kg`}
            </span>
          </div>
        )}
      </div>
      <button type="button" onClick={onClose} style={{ ...primaryBtn, marginTop: 20 }}>Back to training</button>
    </>
  );

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="checkin-heading" style={{ position: 'fixed', inset: 0, zIndex: 1000, background: colors.bg, overflowY: 'auto' }}>
      <style>{css}</style>
      <div key={step} style={{
        maxWidth: 480, minHeight: '100%', margin: '0 auto', boxSizing: 'border-box', padding: '24px 24px 32px',
        display: 'flex', flexDirection: 'column', color: colors.text, fontFamily: "'Inter', system-ui, sans-serif",
      }}>
        {topBar}
        {step === 0 && weightStep}
        {step === 1 && photosStep}
        {step === 2 && messageStep}
        {step === 3 && sentStep}
      </div>
      {scanOpen && createPortal(
        <Dialog title="Add scan" onClose={() => setScanOpen(false)}>
          <PhotoScan clientId={userId} lastMachine={lastMachine} onSaved={scanSaved} onCancel={() => setScanOpen(false)} />
        </Dialog>,
        document.body,
      )}
    </div>
  );
};

export default MonthlyCheckIn;
